import recordingService from '@/services/recordingService';
import store from '@/store';

// ============ SESSION MANAGEMENT ============

/**
 * Create a new live recording session for a patient encounter
 * @param {Object} params
 * @param {number} params.patient_id - Patient ID for this recording
 * @param {number} [params.encounter_id] - Associated encounter note ID
 * @returns {Promise} API response with session details including id
 */
export const createSessionApi = async ({ patient_id, encounter_id }) => {
  try {
    const response = await recordingService.post(`/recording/sessions`, {
      patient_id,
      encounter_id,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create recording session:', error);
    throw error;
  }
};

/**
 * Get session details by ID
 * @param {string|number} sessionId - Recording session ID
 * @returns {Promise} API response with session details
 */
export const getSessionApi = async (sessionId) => {
  try {
    const response = await recordingService.get(`/recording/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get session:', error);
    throw error;
  }
};

/**
 * Pause an active recording session
 * @param {string|number} sessionId - Recording session ID
 * @returns {Promise} API response
 */
export const pauseSessionApi = async (sessionId) => {
  try {
    const response = await recordingService.patch(`/recording/sessions/${sessionId}/pause`);
    return response.data;
  } catch (error) {
    console.error('Failed to pause session:', error);
    throw error;
  }
};

/**
 * Resume a paused recording session
 * @param {string|number} sessionId - Recording session ID
 * @returns {Promise} API response
 */
export const resumeSessionApi = async (sessionId) => {
  try {
    const response = await recordingService.patch(`/recording/sessions/${sessionId}/resume`);
    return response.data;
  } catch (error) {
    console.error('Failed to resume session:', error);
    throw error;
  }
};

/**
 * Stop and finalize a recording session
 * @param {string|number} sessionId - Recording session ID
 * @returns {Promise} API response
 */
export const stopSessionApi = async (sessionId) => {
  try {
    const response = await recordingService.patch(`/recording/sessions/${sessionId}/stop`);
    return response.data;
  } catch (error) {
    console.error('Failed to stop session:', error);
    throw error;
  }
};

/**
 * Delete a recording session permanently
 * Deletes session, audio chunks, R2 audio file, embeddings, and summaries
 * @param {string|number} sessionId - Recording session ID
 * @returns {Promise} API response with { session_id, message }
 */
export const deleteSessionApi = async (sessionId) => {
  try {
    const response = await recordingService.delete(`/recording/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete session:', error);
    throw error;
  }
};

/**
 * Edit and save corrections to transcription
 * @param {string|number} sessionId - Recording session ID
 * @param {string} transcriptionText - Corrected transcription text
 * @returns {Promise} API response
 */
export const updateTranscriptionApi = async (sessionId, transcriptionText) => {
  try {
    const response = await recordingService.put(
      `/recording/sessions/${sessionId}/transcription`,
      { transcription_text: transcriptionText }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update transcription:', error);
    throw error;
  }
};

/**
 * List recording sessions with pagination
 * @param {Object} [params]
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.page_size=20] - Page size (max 100)
 * @returns {Promise} API response with sessions array
 */
export const listSessionsApi = async (params = {}) => {
  try {
    const response = await recordingService.get(`/recording/sessions`, { params });
    return response.data;
  } catch (error) {
    console.error('Failed to list sessions:', error);
    throw error;
  }
};

// ============ AUDIO CHUNKS ============

/**
 * Upload audio chunk for transcription
 * @param {string|number} sessionId - Recording session ID
 * @param {FormData} formData - Form data containing:
 *   - audio: File (webm, wav, mp3) - required
 *   - sequence_number: integer - required
 *   - duration_ms: integer (0-30000) - required
 *   - is_final: "true" | "false" (default: "false")
 *   - is_partial: "true" | "false" (default: "false")
 * @returns {Promise} API response with chunk_id and status
 */
export const uploadChunkApi = async (sessionId, formData) => {
  try {
    // Note: endpoint is /recording/ (singular) not /recording/
    const response = await recordingService.post(
      `/recording/sessions/${sessionId}/chunks`,
      formData
    );
    return response.data;
  } catch (error) {
    console.error('Failed to upload chunk:', error);
    throw error;
  }
};

// ============ TRANSCRIPTIONS ============

/**
 * Get full transcription with chunk details
 * @param {string|number} sessionId - Recording session ID
 * @returns {Promise} API response with full_transcription and segments
 */
export const getTranscriptionApi = async (sessionId) => {
  try {
    const response = await recordingService.get(
      `/recording/sessions/${sessionId}/transcription`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get transcription:', error);
    throw error;
  }
};

/**
 * Create SSE connection for real-time transcription streaming
 * Note: EventSource doesn't support custom headers, so token is passed in URL
 * @param {string|number} sessionId - Recording session ID
 * @returns {EventSource} SSE event source
 */
export const createTranscriptionStream = (sessionId) => {
  const token = store.state.login?.token;
  const baseURL = process.env.VUE_APP_ENV === 'production'
    ? 'https://www.vibrant-america.com/lisapi/v1/ehr/transcription/api/v1'
    : 'https://www.vibrant-america.com/lisapi/v1/ehr/transcription-staging/api/v1';

  const url = `${baseURL}/recording/sessions/${sessionId}/transcriptions/stream?token=${encodeURIComponent(token)}`;
  return new EventSource(url);
};

// ============ SUMMARIES ============

/**
 * Generate AI-powered clinical summary from transcription
 * @param {string|number} sessionId - Recording session ID
 * @param {Object} [options]
 * @param {number} [options.prompt_id] - Prompt template ID
 * @param {string} [options.additional_instructions] - Extra instructions for AI
 * @returns {Promise} API response with summary and summary_record_id
 */
export const generateSummaryApi = async (sessionId, { prompt_id, additional_instructions } = {}) => {
  try {
    const response = await recordingService.post(
      `/recording/sessions/${sessionId}/summary`,
      { prompt_id: prompt_id ?? 1, additional_instructions }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to generate summary:', error);
    throw error;
  }
};

/**
 * Get a specific summary by ID
 * @param {string|number} summaryId - Summary ID
 * @returns {Promise} API response with summary details
 */
export const getSummaryApi = async (summaryId) => {
  try {
    const response = await recordingService.get(`/recording/summaries/${summaryId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get summary:', error);
    throw error;
  }
};

/**
 * Regenerate summary with different prompt/instructions
 * @param {string|number} summaryId - Summary ID
 * @param {Object} [options]
 * @param {number} [options.prompt_id] - New prompt template ID
 * @param {string} [options.additional_instructions] - Extra instructions for AI
 * @returns {Promise} API response with new summary
 */
export const regenerateSummaryApi = async (summaryId, { prompt_id, additional_instructions } = {}) => {
  try {
    const response = await recordingService.post(
      `/recording/summaries/${summaryId}/regenerate`,
      { prompt_id, additional_instructions }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to regenerate summary:', error);
    throw error;
  }
};

/**
 * Get all summary versions for a session
 * @param {string|number} sessionId - Recording session ID
 * @returns {Promise} API response with summaries array
 */
export const getSummaryHistoryApi = async (sessionId) => {
  try {
    const response = await recordingService.get(
      `/recording/sessions/${sessionId}/summaries`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get summary history:', error);
    throw error;
  }
};

/**
 * Get most recent summary for a patient
 * @param {string|number} patientId - Patient ID
 * @returns {Promise} API response with latest summary
 */
export const getLatestPatientSummaryApi = async (patientId) => {
  try {
    const response = await recordingService.get(
      `/recording/summaries/patient/${patientId}/latest`
    );
    return response.data;
  } catch (error) {
    // 404 means no summaries exist yet, which is a valid state
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Failed to get latest patient summary:', error);
    throw error;
  }
};

// ============ PROMPTS ============

/**
 * List all prompt templates available to the authenticated doctor
 * @returns {Promise} API response with prompts array
 */
export const getPromptsApi = async () => {
  try {
    const response = await recordingService.get(`/prompts`);
    return response.data;
  } catch (error) {
    console.error('Failed to get prompts:', error);
    throw error;
  }
};

/**
 * Get a specific prompt template by ID
 * @param {string|number} promptId - Prompt ID
 * @returns {Promise} API response with prompt details
 */
export const getPromptApi = async (promptId) => {
  try {
    const response = await recordingService.get(`/prompts/${promptId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get prompt:', error);
    throw error;
  }
};

/**
 * Get the default prompt for the authenticated doctor
 * @returns {Promise} API response with default prompt
 */
export const getDefaultPromptApi = async () => {
  try {
    const response = await recordingService.get(`/prompts/default`);
    return response.data;
  } catch (error) {
    console.error('Failed to get default prompt:', error);
    throw error;
  }
};

/**
 * Create a new custom prompt template
 * @param {Object} data
 * @param {string} data.name - Prompt name
 * @param {string} [data.description] - Prompt description
 * @param {string} data.system_prompt - System prompt for AI
 * @param {string} data.user_prompt_template - User prompt template
 * @param {string} [data.category] - Category (e.g., "clinical_documentation")
 * @param {string[]} [data.tags] - Tags for categorization
 * @param {boolean} [data.is_default] - Set as default prompt
 * @returns {Promise} API response with created prompt
 */
export const createPromptApi = async (data) => {
  try {
    const response = await recordingService.post(`/prompts`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to create prompt:', error);
    throw error;
  }
};

/**
 * Update a custom prompt template
 * @param {string|number} promptId - Prompt ID
 * @param {Object} data - Fields to update
 * @returns {Promise} API response
 */
export const updatePromptApi = async (promptId, data) => {
  try {
    const response = await recordingService.put(`/prompts/${promptId}`, data);
    return response.data;
  } catch (error) {
    console.error('Failed to update prompt:', error);
    throw error;
  }
};

/**
 * Delete a custom prompt template
 * @param {string|number} promptId - Prompt ID
 * @returns {Promise} API response
 */
export const deletePromptApi = async (promptId) => {
  try {
    const response = await recordingService.delete(`/prompts/${promptId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to delete prompt:', error);
    throw error;
  }
};

/**
 * Set a prompt as the default for the authenticated doctor
 * @param {string|number} promptId - Prompt ID
 * @param {boolean} isDefault - Whether to set as default
 * @returns {Promise} API response
 */
export const setDefaultPromptApi = async (promptId, isDefault = true) => {
  try {
    const response = await recordingService.patch(`/prompts/${promptId}/default`, {
      is_default: isDefault,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to set default prompt:', error);
    throw error;
  }
};

// ============ ENCOUNTER QUERIES ============

/**
 * Get all sessions for an encounter
 * @param {string|number} encounterId - Encounter note ID
 * @returns {Promise} API response with sessions array
 */
export const getEncounterSessionsApi = async (encounterId) => {
  try {
    const response = await recordingService.get(
      `/recording/encounters/${encounterId}/sessions`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get encounter sessions:', error);
    throw error;
  }
};

/**
 * Get full transcription for an encounter
 * @param {string|number} encounterId - Encounter note ID
 * @returns {Promise} API response with transcription
 */
export const getEncounterTranscriptionApi = async (encounterId) => {
  try {
    const response = await recordingService.get(
      `/recording/encounters/${encounterId}/transcription`
    );
    return response;
  } catch (error) {
    console.error('Failed to get encounter transcription:', error);
    throw error;
  }
};

/**
 * Get audio file for a specific session
 * Uses token as query param since <audio src> cannot send headers
 * @param {string|number} sessionId - Recording session ID
 * @returns {Promise<Blob|null>} Audio blob or null if not available
 */
export const getSessionAudioApi = async (sessionId) => {
  try {
    const token = store.state.login?.token;
    const response = await recordingService.get(
      `/recording/sessions/${sessionId}/audio`,
      { params: { token }, responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 204 || error.response?.status === 404) {
      return null;
    }
    console.error('Failed to get session audio:', error);
    throw error;
  }
};


/**
 * Get all summaries for an encounter
 * @param {string|number} encounterId - Encounter note ID
 * @returns {Promise} API response with summaries array
 */
export const getEncounterSummariesApi = async (encounterId) => {
  try {
    const response = await recordingService.get(
      `/recording/encounters/${encounterId}/summaries`
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get encounter summaries:', error);
    throw error;
  }
};

// ============ BATCH UPLOAD ============

/**
 * Upload a large audio file for batch transcription
 * @param {FormData} formData - Form data containing:
 *   - audio_file: File (MP3, WAV, M4A, WebM, FLAC, AAC, OGG) - required
 *   - patient_id: number - required
 *   - encounter_id: number - optional
 * @returns {Promise} API response with session_id and status_url
 */
export const batchUploadApi = async (formData) => {
  try {
    const response = await recordingService.post(`/batch/upload`, formData);
    return response.data;
  } catch (error) {
    console.error('Failed to upload batch file:', error);
    throw error;
  }
};

/**
 * Check the status of a batch transcription job
 * @param {string|number} sessionId - Session ID from batch upload
 * @returns {Promise} API response with status and progress
 */
export const getBatchStatusApi = async (sessionId) => {
  try {
    const response = await recordingService.get(`/batch/status/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get batch status:', error);
    throw error;
  }
};

// ============ ZOOM IMPORT ============

/**
 * Import a Zoom recording for transcription
 * @param {Object} params
 * @param {string} params.recording_url - Zoom recording URL
 * @param {string} [params.password] - Recording passcode (optional, include if password-protected)
 * @param {string} params.patient_id - Patient ID
 * @param {string} [params.encounter_id] - Encounter note ID
 * @returns {Promise} 202 response with { session_id, status, patient_id, encounter_id, message }
 */
export const importZoomRecordingApi = async ({ recording_url, password, patient_id, encounter_id }) => {
  try {
    const payload = { recording_url, patient_id };
    if (encounter_id) payload.encounter_id = encounter_id;
    if (password) payload.password = password;
    const response = await recordingService.post(`/zoom/transcribe`, payload);
    return response.data;
  } catch (error) {
    console.error('Failed to import Zoom recording:', error);
    throw error;
  }
};

/**
 * Poll encounter-level transcription for import status
 * Returns 204 if still processing, 200 with session data when done
 * Check session_status and error_code for password/failure handling
 * @param {string|number} encounterId - Encounter note ID
 * @returns {Promise} API response with transcription and status
 */
export const pollImportTranscriptionApi = async (encounterId) => {
  try {
    const response = await recordingService.get(
      `/recording/encounters/${encounterId}/transcription`
    );
    // 204 passes through the interceptor but has no body — means still processing
    if (response.status === 204 || !response.data) {
      return { _polling: true };
    }
    return response.data;
  } catch (error) {
    if (error.response?.status === 204) {
      return { _polling: true };
    }
    throw error;
  }
};

// ============ SEARCH & EMBEDDINGS ============

/**
 * Perform semantic search across transcripts
 * @param {Object} params
 * @param {string} params.query - Search query
 * @param {number} [params.limit=10] - Max results
 * @param {number} [params.patient_id] - Filter by patient
 * @param {number} [params.encounter_id] - Filter by encounter
 * @param {number} [params.session_id] - Filter by session
 * @param {string} [params.speaker_label] - Filter by speaker
 * @param {string} [params.date_from] - Filter by date range start
 * @param {string} [params.date_to] - Filter by date range end
 * @returns {Promise} API response with search results
 */
export const searchTranscriptsApi = async (params) => {
  try {
    const response = await recordingService.post(`/search`, params);
    return response.data;
  } catch (error) {
    console.error('Failed to search transcripts:', error);
    throw error;
  }
};

/**
 * Get embedding statistics for clinic
 * @returns {Promise} API response with stats
 */
export const getEmbeddingStatsApi = async () => {
  try {
    const response = await recordingService.get(`/search/stats`);
    return response.data;
  } catch (error) {
    console.error('Failed to get embedding stats:', error);
    throw error;
  }
};
